import React from 'react';
import PropTypes from 'prop-types';
import { branch, hoistStatics, pure } from 'recompose';

import classNames from 'classnames';
import * as styles from './styles.css';
import IndicatorIcon from './IndicatorIcon';
import InfoTooltip from '../InfoToolTip';

/**
 * Form Control, wraps most components as a HOC to add label, info-text etc
 */

const Label = pure(({ label, info }) => (
  <span className="control-label">
    {label}
    {info && <InfoTooltip title={label}>{info}</InfoTooltip>}
  </span>
));

const formControl =
  (InputComponent) => {
    const wrappedComponent =
      (props) => {
        const TagName = props.isLabel ? 'label' : 'div';
        const isDirty = (props.meta.touched || props.meta.dirty || props.meta.submitFailed);
        if (props.label) {
          return (
            <TagName
              className={classNames(
                'form-group',
                styles.formControl,
                { [styles.noBottomMargin]: props.noBottomMargin },
                { 'has-error': isDirty && props.meta.error },
                { 'has-warning': isDirty && props.meta.warning },
              )}
              htmlFor={props.input ? props.input.name : undefined}
            >
              {props.type !== 'checkbox' && (
                <Label label={props.label} info={props.info} />
              )}
              <div
                data-testid={props.testID}
                className={styles.inputContainer}
                title={props.disabled && props.disabledLabel ? props.disabledLabel : undefined}
              >
                <InputComponent {...props} />
                {props.type === 'checkbox' && (
                  <Label label={props.label} info={props.info} />
                )}
                {!props.noIndicator && (
                  <IndicatorIcon tooltipPlacement={props.tooltipPlacement} label={props.label} {...props.meta} />
                )}
              </div>
              {props.helpText && (
                <p className="help-block">{props.helpText}</p>
              )}
            </TagName>
          );
        }

        return (
          <div
            className={classNames(
              styles.formControl,
              { 'has-error': isDirty && props.meta.error },
              { 'has-warning': isDirty && props.meta.warning },
            )}
          >
            <div
              data-testid={props.testID}
              className={styles.inputContainer}
              title={props.disabled && props.disabledLabel ? props.disabledLabel : undefined}
            >
              <InputComponent {...props} />
              {!props.noIndicator && (
                <IndicatorIcon tooltipPlacement={props.tooltipPlacement} label={props.label} {...props.meta}/>
              )}
            </div>
          </div>
        );
      };

    const blacklist = ['propTypes', 'defaultProps'];
    Object.keys(InputComponent)
      .filter(k => !blacklist.includes(k))
      .forEach((k) => {
        wrappedComponent[k] = InputComponent[k];
      });

    wrappedComponent.defaultProps = {
      ...formControl.defaultProps,
      ...InputComponent.defaultProps,
    };

    wrappedComponent.propTypes = {
      ...formControl.propTypes,
      ...InputComponent.propTypes,
    };

    return wrappedComponent;
  };


formControl.defaultProps = {
  label: '',
  meta: {},
  formControl: true,
  testID: undefined,
  isLabel: true,
  noIndicator: false,
  noBottomMargin: false,
  helpText: undefined,
};

formControl.propTypes = {
  label: PropTypes.string,
  formControl: PropTypes.bool,
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.string,
    warning: PropTypes.string,
  }).isRequired,
  testID: PropTypes.string,
  isLabel: PropTypes.bool,
  noIndicator: PropTypes.bool,
  noBottomMargin: PropTypes.bool,
  helpText: PropTypes.string,
};

export default hoistStatics(branch(
  props => props.formControl !== false,
  formControl,
));

